import * as THREE from 'three';
import { Global } from '../common/global';
import gsap from 'gsap';

const _VS = `
uniform float pointMultiplier;

attribute float size;
attribute float angle;
attribute vec4 aColor;

varying vec4 vColor;
varying vec2 vAngle;

void main() {
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);

  gl_Position = projectionMatrix * mvPosition;
  gl_PointSize = size * pointMultiplier / gl_Position.w;

  vAngle = vec2(cos(angle), sin(angle));
  vColor = aColor;
}`;

const _FS = `
uniform sampler2D diffuseTexture;

varying vec4 vColor;
varying vec2 vAngle;

void main() {
  vec2 coords = (gl_PointCoord - 0.5) * mat2(vAngle.x, vAngle.y, -vAngle.y, vAngle.x) + 0.5;
  gl_FragColor = texture2D(diffuseTexture, coords) * vColor;
}`;

function getLinearSpline(lerp) {
  const points = [];
  const _lerp = lerp;

  function addPoint(t, d) {
    points.push([t, d]);
  }

  function getValueAt(t) {
    let p1 = 0;

    for (let i = 0; i < points.length; i++) {
      if (points[i][0] >= t) {
        break;
      }
      p1 = i;
    }

    const p2 = Math.min(points.length - 1, p1 + 1);

    if (p1 == p2) {
      return points[p1][1];
    }

    return _lerp(
      (t - points[p1][0]) / (points[p2][0] - points[p1][0]),
      points[p1][1],
      points[p2][1]
    );
  }
  return { addPoint, getValueAt };
}

function getParticleSystem(params) {
  const { camera, emitter, parent, rate, texture } = params;

  const uniforms = {
    diffuseTexture: {
      value: Global.assets['textures'][texture]['asset'],
    },
    pointMultiplier: {
      value: window.innerHeight / (2.0 * Math.tan((30.0 * Math.PI) / 180.0)),
    },
  };

  let _material = new THREE.ShaderMaterial({
    uniforms: uniforms,
    vertexShader: _VS,
    fragmentShader: _FS,
    blending: THREE.AdditiveBlending,
    depthTest: false,
    depthWrite: false,
    transparent: true,
    vertexColors: true,
  });

  let _particles = [];

  let geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute([], 3));
  geometry.setAttribute('size', new THREE.Float32BufferAttribute([], 1));
  geometry.setAttribute('aColor', new THREE.Float32BufferAttribute([], 4));
  geometry.setAttribute('angle', new THREE.Float32BufferAttribute([], 1));

  let _points = new THREE.Points(geometry, _material);
  parent.add(_points);
  _points.frustumCulled = false;

  //
  const alphaSpline = getLinearSpline((t, a, b) => {
    return a + t * (b - a);
  });
  alphaSpline.addPoint(0.0, 0.0);
  alphaSpline.addPoint(0.75, 1.0);
  alphaSpline.addPoint(1.0, 0.0);

  const colorSpline = getLinearSpline((t, a, b) => {
    const c = a.clone();
    return c.lerp(b, t);
  });
  colorSpline.addPoint(1, new THREE.Color('#71d83f'));
  // colorSpline.addPoint(1, new THREE.Color(0x00ff00));

  const sizeSpline = getLinearSpline((t, a, b) => {
    return a + t * (b - a);
  });
  sizeSpline.addPoint(0.0, 0.0);
  sizeSpline.addPoint(1.0, 1.0);

  const radius = 0.25;
  const maxLife = 0.4;
  const maxSize = 0.25;
  const maxVelocity = 0.2;
  let gdfsghk = 0.1;

  // emission / dispose control
  let _emitting = true;
  let _disposing = false;
  let _disposePromise = null;
  const _maxDisposeWait = 5000; // ms - max wait for particles to settle

  function _AddParticles(timeElapsed) {
    if (!_emitting) return;
    gdfsghk += timeElapsed;
    const n = Math.floor(gdfsghk * rate);
    gdfsghk -= n / rate;
    for (let i = 0; i < n; i += 1) {
      const life = (Math.random() * 0.75 + 0.5) * maxLife;
      _particles.push({
        position: new THREE.Vector3(
          (Math.random() * 2 - 1) * radius,
          (Math.random() * 2 - 1) * radius,
          (Math.random() * 2 - 1) * radius
        ).add(emitter.position),
        size: (Math.random() * 0.5 + 0.5) * maxSize,
        colour: new THREE.Color(),
        alpha: 1.0,
        life: life,
        maxLife: life,
        rotation: Math.random() * 2.0 * Math.PI,
        rotationRate: Math.random() * 0.01 - 0.005,
        velocity: new THREE.Vector3(
          (Math.random() * 3 - 1.5) * maxVelocity,
          (Math.random() * 3 - 1.5) * maxVelocity,
          (Math.random() * 3 - 1.5) * maxVelocity
        ),
      });
    }
  }

  function _UpdateGeometry() {
    // guard: geometry may be nulled during dispose, avoid calling setAttribute on null
    if (!geometry || typeof geometry.setAttribute !== 'function') return;

    const positions = [];
    const sizes = [];
    const colours = [];
    const angles = [];

    for (let p of _particles) {
      positions.push(p.position.x, p.position.y, p.position.z);
      colours.push(p.colour.r, p.colour.g, p.colour.b, p.alpha);
      sizes.push(p.currentSize);
      angles.push(p.rotation);
    }

    // update geometry attributes (recreate attributes to match particle count)
    geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(positions, 3)
    );
    geometry.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1));
    geometry.setAttribute(
      'aColor',
      new THREE.Float32BufferAttribute(colours, 4)
    );
    geometry.setAttribute('angle', new THREE.Float32BufferAttribute(angles, 1));

    if (geometry.attributes.position)
      geometry.attributes.position.needsUpdate = true;
    if (geometry.attributes.size) geometry.attributes.size.needsUpdate = true;
    if (geometry.attributes.aColor)
      geometry.attributes.aColor.needsUpdate = true;
    if (geometry.attributes.angle) geometry.attributes.angle.needsUpdate = true;
  }
  _UpdateGeometry();

  function _UpdateParticles(timeElapsed) {
    for (let p of _particles) {
      p.life -= timeElapsed;
    }

    _particles = _particles.filter((p) => p.life > 0.0);

    for (let p of _particles) {
      const t = 1.0 - p.life / p.maxLife;
      p.rotation += p.rotationRate;
      p.alpha = alphaSpline.getValueAt(t);
      p.currentSize = p.size * sizeSpline.getValueAt(t);
      p.colour.copy(colorSpline.getValueAt(t));

      // simple Euler integration
      p.position.addScaledVector(p.velocity, timeElapsed);

      // simple drag (in-place)
      p.velocity.x -=
        Math.sign(p.velocity.x) *
        Math.min(
          Math.abs(p.velocity.x),
          Math.abs(p.velocity.x) * timeElapsed * 0.1
        );
      p.velocity.y -=
        Math.sign(p.velocity.y) *
        Math.min(
          Math.abs(p.velocity.y),
          Math.abs(p.velocity.y) * timeElapsed * 0.1
        );
      p.velocity.z -=
        Math.sign(p.velocity.z) *
        Math.min(
          Math.abs(p.velocity.z),
          Math.abs(p.velocity.z) * timeElapsed * 0.1
        );
    }

    // keep painter's order by distance (descending)
    _particles.sort((a, b) => {
      const d1 = camera.position.distanceTo(a.position);
      const d2 = camera.position.distanceTo(b.position);

      if (d1 > d2) return -1;
      if (d1 < d2) return 1;
      return 0;
    });
  }

  function update(timeElapsed) {
    // If geometry/points were removed already (dispose in progress/completed), skip updates
    if (!geometry || !_points) return;

    _AddParticles(timeElapsed);
    _UpdateParticles(timeElapsed);
    _UpdateGeometry();
  }

  // dispose: stop emitting, wait for particles to die out (or timeout), then cleanup.
  function dispose() {
    if (_disposing) return _disposePromise;
    _disposing = true;
    _emitting = false;

    _disposePromise = new Promise((resolve) => {
      const start = performance.now();
      const check = () => {
        const elapsed = performance.now() - start;
        if (_particles.length === 0 || elapsed >= _maxDisposeWait) {
          try {
            if (parent && _points && parent.remove) parent.remove(_points);

            if (geometry && typeof geometry.dispose === 'function') {
              geometry.dispose();
            }

            // Don't dispose shared textures from Global.assets here.
            if (_material && typeof _material.dispose === 'function') {
              _material.dispose();
            }
          } catch (e) {
            // ignore cleanup errors
          }

          // clear arrays and break references
          _particles.length = 0;
          try {
            if (_points) {
              _points.geometry = null;
              _points.material = null;
            }
          } catch (e) {}
          // allow GC by nulling local references where possible
          geometry = null;
          _material = null;
          _points = null;
          resolve();
        } else {
          requestAnimationFrame(check);
        }
      };
      requestAnimationFrame(check);
    });

    return _disposePromise;
  }

  return { update, dispose };
}

export { getParticleSystem };
